import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Bell, CalendarCheck, Inbox, BellRing, BellOff, X, CheckCheck, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type NotifKind = "appointment" | "contact";
type FilterKind = "unread" | "today" | "week";

interface NotifItem {
  id: string;
  kind: NotifKind;
  title: string;
  subtitle: string;
  createdAt: string;
}

const SEEN_KEY = "egs.admin.notifLastSeen";
const SOUND_KEY = "egs.admin.notifSound";
const BROWSER_KEY = "egs.admin.notifBrowser";
const DISMISSED_KEY = "egs.admin.notifDismissed";
const FILTER_KEY = "egs.admin.notifFilter";

const DAY = 86_400_000;

// Simple ding using WebAudio (no asset required)
const playDing = () => {
  try {
    const Ctx =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    o.start();
    o.stop(ctx.currentTime + 0.55);
    setTimeout(() => ctx.close(), 800);
  } catch {
    /* ignore */
  }
};

const showBrowserNotif = (title: string, body: string) => {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification(title, { body, icon: "/favicon.ico", tag: "egs-admin" });
    }
  } catch {
    /* ignore */
  }
};

const relativeTime = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < DAY) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const loadDismissed = (): Set<string> => {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
};

const saveDismissed = (s: Set<string>) => {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(s)));
};

const NotificationCenter = () => {
  const [items, setItems] = useState<NotifItem[]>([]);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());
  const [filter, setFilter] = useState<FilterKind>(() => {
    if (typeof window === "undefined") return "unread";
    return (localStorage.getItem(FILTER_KEY) as FilterKind) || "unread";
  });
  const [lastSeen, setLastSeen] = useState<number>(() => {
    const v = typeof window !== "undefined" ? localStorage.getItem(SEEN_KEY) : null;
    return v ? Number(v) : 0;
  });
  const [soundOn, setSoundOn] = useState<boolean>(() =>
    typeof window === "undefined" ? true : localStorage.getItem(SOUND_KEY) !== "0"
  );
  const [browserOn, setBrowserOn] = useState<boolean>(() =>
    typeof window === "undefined" ? false : localStorage.getItem(BROWSER_KEY) === "1"
  );
  const initialLoad = useRef(true);
  const panelRef = useRef<HTMLDivElement>(null);

  // Only show items from the last 7 days, not dismissed
  const visibleItems = useMemo(() => {
    const cutoff = Date.now() - 7 * DAY;
    return items.filter(
      (i) => !dismissed.has(i.id) && new Date(i.createdAt).getTime() >= cutoff
    );
  }, [items, dismissed]);

  const filteredItems = useMemo(() => {
    const now = Date.now();
    return visibleItems.filter((i) => {
      const t = new Date(i.createdAt).getTime();
      if (filter === "unread") return t > lastSeen;
      if (filter === "today") return now - t < DAY;
      return now - t < 7 * DAY;
    });
  }, [visibleItems, filter, lastSeen]);

  const unreadCount = visibleItems.filter(
    (i) => new Date(i.createdAt).getTime() > lastSeen
  ).length;

  const setFilterPersist = (f: FilterKind) => {
    setFilter(f);
    localStorage.setItem(FILTER_KEY, f);
  };

  const markAllRead = useCallback(() => {
    const now = Date.now();
    setLastSeen(now);
    localStorage.setItem(SEEN_KEY, String(now));
  }, []);

  const dismissOne = (id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(next);
      return next;
    });
  };

  const clearAll = () => {
    setDismissed((prev) => {
      const next = new Set(prev);
      visibleItems.forEach((i) => next.add(i.id));
      saveDismissed(next);
      return next;
    });
    markAllRead();
  };

  const loadRecent = useCallback(async () => {
    const sinceIso = new Date(Date.now() - 7 * DAY).toISOString();
    const [a, c] = await Promise.all([
      supabase
        .from("appointments")
        .select("id,name,email,slot_start,created_at,status")
        .gte("created_at", sinceIso)
        .order("created_at", { ascending: false })
        .limit(25),
      supabase
        .from("contact_submissions")
        .select("id,name,email,message,created_at")
        .gte("created_at", sinceIso)
        .order("created_at", { ascending: false })
        .limit(25),
    ]);
    const next: NotifItem[] = [];
    a.data?.forEach((r) => {
      next.push({
        id: `appt:${r.id}`,
        kind: "appointment",
        title: `New appointment, ${r.name}`,
        subtitle: `${new Date(r.slot_start).toLocaleString()}, ${r.email}`,
        createdAt: r.created_at,
      });
    });
    c.data?.forEach((r) => {
      next.push({
        id: `contact:${r.id}`,
        kind: "contact",
        title: `New message, ${r.name}`,
        subtitle: r.message?.slice(0, 80) || r.email,
        createdAt: r.created_at,
      });
    });
    next.sort((x, y) => new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime());
    setItems(next.slice(0, 50));
    initialLoad.current = false;
  }, []);

  // Initial load
  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // Polling (no realtime on PII tables)
  useEffect(() => {
    const handleAppt = (payload: { new: Record<string, unknown> }) => {
      const r = payload.new as { id: string; name: string; email: string; slot_start: string; created_at: string };
      const item: NotifItem = {
        id: `appt:${r.id}`,
        kind: "appointment",
        title: `New appointment, ${r.name}`,
        subtitle: `${new Date(r.slot_start).toLocaleString()}, ${r.email}`,
        createdAt: r.created_at,
      };
      setItems((prev) => [item, ...prev.filter((p) => p.id !== item.id)].slice(0, 50));
      if (soundOn) playDing();
      if (browserOn) showBrowserNotif(item.title, item.subtitle);
      toast.success(item.title, { description: item.subtitle });
    };
    const handleContact = (payload: { new: Record<string, unknown> }) => {
      const r = payload.new as { id: string; name: string; email: string; message: string; created_at: string };
      const item: NotifItem = {
        id: `contact:${r.id}`,
        kind: "contact",
        title: `New message, ${r.name}`,
        subtitle: r.message?.slice(0, 80) || r.email,
        createdAt: r.created_at,
      };
      setItems((prev) => [item, ...prev.filter((p) => p.id !== item.id)].slice(0, 50));
      if (soundOn) playDing();
      if (browserOn) showBrowserNotif(item.title, item.subtitle);
      toast.success(item.title, { description: item.subtitle });
    };

    const seen = new Set<string>();
    const poll = async () => {
      if (document.visibilityState !== "visible") return;
      const since = new Date(Date.now() - 5 * 60_000).toISOString();
      const [{ data: appts }, { data: contacts }] = await Promise.all([
        supabase.from("appointments")
          .select("id,name,email,slot_start,created_at")
          .gte("created_at", since).order("created_at", { ascending: false }),
        supabase.from("contact_submissions")
          .select("id,name,email,message,created_at")
          .gte("created_at", since).order("created_at", { ascending: false }),
      ]);
      (appts || []).forEach((r) => {
        const key = `appt:${r.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        handleAppt({ new: r as Record<string, unknown> });
      });
      (contacts || []).forEach((r) => {
        const key = `contact:${r.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        handleContact({ new: r as Record<string, unknown> });
      });
    };
    (async () => {
      const since = new Date(Date.now() - 5 * 60_000).toISOString();
      const [{ data: a }, { data: c }] = await Promise.all([
        supabase.from("appointments").select("id").gte("created_at", since),
        supabase.from("contact_submissions").select("id").gte("created_at", since),
      ]);
      (a || []).forEach((r: { id: string }) => seen.add(`appt:${r.id}`));
      (c || []).forEach((r: { id: string }) => seen.add(`contact:${r.id}`));
    })();
    const id = window.setInterval(poll, 30_000);
    return () => window.clearInterval(id);
  }, [soundOn, browserOn]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem(SOUND_KEY, next ? "1" : "0");
    if (next) playDing();
  };

  const enableBrowser = async () => {
    if (!("Notification" in window)) {
      toast.error("This browser does not support notifications");
      return;
    }
    if (browserOn) {
      setBrowserOn(false);
      localStorage.setItem(BROWSER_KEY, "0");
      return;
    }
    if (Notification.permission === "denied") {
      toast.error(
        "Notifications are blocked. Click the lock icon in your address bar, Site settings, allow Notifications, then try again.",
        { duration: 8000 }
      );
      return;
    }
    try {
      const perm =
        Notification.permission === "granted"
          ? "granted"
          : await Notification.requestPermission();
      if (perm === "granted") {
        setBrowserOn(true);
        localStorage.setItem(BROWSER_KEY, "1");
        showBrowserNotif("Notifications enabled", "You'll be alerted when new leads come in.");
      } else if (perm === "denied") {
        toast.error(
          "You denied notifications. Enable them in your browser's site settings to receive lead alerts.",
          { duration: 7000 }
        );
      } else {
        toast("Notification request dismissed. You can enable it anytime.");
      }
    } catch {
      toast.error("Could not request notification permission in this preview. Try on the published site.");
    }
  };

  const filterTabs: { key: FilterKind; label: string; count: number }[] = [
    { key: "unread", label: "Unread", count: visibleItems.filter((i) => new Date(i.createdAt).getTime() > lastSeen).length },
    { key: "today", label: "Today", count: visibleItems.filter((i) => Date.now() - new Date(i.createdAt).getTime() < DAY).length },
    { key: "week", label: "7 days", count: visibleItems.length },
  ];

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors hover:bg-[hsl(0_0%_10%)]"
        style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 80%)" }}
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span
            className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
            style={{ background: "hsl(0 75% 55%)" }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-[380px] overflow-hidden rounded-xl border shadow-2xl"
          style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 18%)" }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between border-b px-4 py-3"
            style={{ borderColor: "hsl(0 0% 14%)" }}
          >
            <div>
              <p className="text-sm font-semibold" style={{ color: "hsl(0 0% 92%)" }}>
                Notifications
              </p>
              <p className="text-[10px]" style={{ color: "hsl(0 0% 50%)" }}>
                Showing last 7 days
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleSound}
                title={soundOn ? "Mute sound" : "Enable sound"}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-[hsl(0_0%_12%)]"
                style={{ color: soundOn ? "hsl(207 68% 60%)" : "hsl(0 0% 45%)" }}
              >
                {soundOn ? <BellRing size={14} /> : <BellOff size={14} />}
              </button>
              <button
                onClick={enableBrowser}
                title={browserOn ? "Disable browser notifications" : "Enable browser notifications"}
                className="rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors"
                style={{
                  background: browserOn ? "hsl(207 68% 50% / 0.15)" : "transparent",
                  color: browserOn ? "hsl(207 68% 65%)" : "hsl(0 0% 55%)",
                  border: `1px solid ${browserOn ? "hsl(207 68% 50% / 0.4)" : "hsl(0 0% 20%)"}`,
                }}
              >
                {browserOn ? "On" : "Browser"}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-[hsl(0_0%_12%)]"
                style={{ color: "hsl(0 0% 55%)" }}
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-1 border-b px-3 py-2" style={{ borderColor: "hsl(0 0% 14%)" }}>
            {filterTabs.map((t) => {
              const active = filter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setFilterPersist(t.key)}
                  className="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                  style={{
                    background: active ? "hsl(207 68% 50% / 0.18)" : "transparent",
                    color: active ? "hsl(207 68% 75%)" : "hsl(0 0% 60%)",
                    border: `1px solid ${active ? "hsl(207 68% 50% / 0.4)" : "transparent"}`,
                  }}
                >
                  {t.label}
                  <span
                    className="rounded-full px-1.5 py-0.5 text-[10px] tabular-nums"
                    style={{
                      background: active ? "hsl(207 68% 50% / 0.25)" : "hsl(0 0% 12%)",
                      color: active ? "hsl(207 68% 80%)" : "hsl(0 0% 55%)",
                    }}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  title="Mark all as read"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium transition-colors hover:bg-[hsl(0_0%_12%)]"
                  style={{ color: "hsl(0 0% 70%)" }}
                >
                  <CheckCheck size={12} /> Read
                </button>
              )}
              {visibleItems.length > 0 && (
                <button
                  onClick={clearAll}
                  title="Clear all"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium transition-colors hover:bg-[hsl(0_75%_55%/0.1)]"
                  style={{ color: "hsl(0 70% 65%)" }}
                >
                  <Trash2 size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
                <Bell size={20} style={{ color: "hsl(0 0% 30%)" }} />
                <p className="text-sm font-medium" style={{ color: "hsl(0 0% 70%)" }}>
                  {filter === "unread"
                    ? "You're all caught up"
                    : filter === "today"
                    ? "Nothing today"
                    : "No notifications this week"}
                </p>
                <p className="text-xs" style={{ color: "hsl(0 0% 45%)" }}>
                  New leads and appointments will appear here.
                </p>
              </div>
            ) : (
              filteredItems.map((n) => {
                const isUnread = new Date(n.createdAt).getTime() > lastSeen;
                const Icon = n.kind === "appointment" ? CalendarCheck : Inbox;
                const tint = n.kind === "appointment" ? "hsl(142 70% 55%)" : "hsl(207 68% 60%)";
                return (
                  <div
                    key={n.id}
                    className="group flex items-start gap-3 border-b px-4 py-3 transition-colors hover:bg-[hsl(0_0%_10%)]"
                    style={{
                      borderColor: "hsl(0 0% 12%)",
                      background: isUnread ? "hsl(207 68% 50% / 0.06)" : "transparent",
                    }}
                  >
                    <div
                      className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                      style={{ background: "hsl(0 0% 12%)", color: tint }}
                    >
                      <Icon size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p
                          className="truncate text-sm font-medium"
                          style={{ color: "hsl(0 0% 92%)" }}
                        >
                          {n.title}
                        </p>
                        {isUnread && (
                          <span
                            className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                            style={{ background: "hsl(207 68% 50% / 0.2)", color: "hsl(207 68% 75%)" }}
                          >
                            New
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs" style={{ color: "hsl(0 0% 60%)" }}>
                        {n.subtitle}
                      </p>
                      <p className="mt-1 text-[10px]" style={{ color: "hsl(0 0% 42%)" }}>
                        {relativeTime(n.createdAt)}
                      </p>
                    </div>
                    <button
                      onClick={() => dismissOne(n.id)}
                      title="Dismiss"
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                      style={{ color: "hsl(0 0% 50%)" }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
